import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import 'rsuite/dist/rsuite.min.css';
import { DateRangePicker } from 'rsuite';
import {
    Table,
    Button,
    SpinnerCircular,
    Notification,
} from '@weirgroup/weir-dls'

import { getTableHeaders, groupData } from '../../utils/table.util'
import {
    SELECTED_ASSET,
    locationHistoryTableHeaders,
} from '../../constants/bucket.contants'
import {
    BucketDataType,
    LocationHistoryParamsType,
    LocationHistoryType,
    SelectedPingType,
    TagType,
} from '../../types/bucketTypes'

import { DisplayMap } from '../../components/map/map'
import assetsApi from '../../api/assets.api'
import { routePath } from '../../constants/navigation.constants'
import { StorageService, ACCESS_TKN_KEY, REFRESH_TOKEN } from '../../services/storage_service'
import { useGlobalError } from '../../contexts/global-error.context'

import './styles.scss'
import { fetchBucketList } from '../../utils/getTagData.utils'
import { useDarkMode } from '../../hooks/use-darkmode'
import { useAnalyticsEvent } from '../../hooks/useAnalyticsEvent';

interface Props {
    selectedAsset: BucketDataType
}

type ToastDataType = {
    variant: string
    message: string
}

type PaginationDataType = {
    hasNextPage: boolean
    endCursor: string
    startCursor: string
    hasPreviousPage: boolean
}
export const LocationHistoryPage = (props: Props) => {
    const logAnalyticsEvent = useAnalyticsEvent();
    const { selectedAsset: propSelectedAsset } = props
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { showError } = useGlobalError()

    const [selectedAsset, setSelectedAsset] = useState<any | null>(propSelectedAsset || null)
    const sevenDaysAgo = dayjs(dayjs()).subtract(7, 'days')
    const currentPage = 1
    const rowCount = 5
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isPaginationLoading, setIsPaginationLoading] = useState<boolean>(false)
    const [locationHistory, setLocationHistory] = useState<
        LocationHistoryType[]
    >([])
    const [selectedPing, setSelectedPing] = useState<
        SelectedPingType | undefined
    >(undefined)
    const [showToast, setShowToast] = useState<boolean>(false)

    const [toastData, setToastData] = useState<ToastDataType>()
    const [tagData, setTagData] = useState<TagType>()
    const [dateRange, setDateRange] = useState<[Date, Date]>([sevenDaysAgo.toDate(), dayjs().toDate()])
    const { darkMode } = useDarkMode();
    const [paginationData, setPaginationData] = useState<PaginationDataType>({
        hasNextPage: false,
        endCursor: "",
        startCursor: "",
        hasPreviousPage: false
    })
    useEffect(() => {
        logAnalyticsEvent('web_user_viewed_location_history_page', { description: 'User viewed location history page' });
    }, []);

    useEffect(() => {
        // Get the selected asset using the reusable function
        const asset = getSelectedAsset(selectedAsset, setSelectedAsset);

        if (asset) {
            // Fetch bucket list data only if we have a valid asset
            (async () => {
                try {
                    const data = await fetchBucketList(asset);
                    setTagData(data.fetchedAssets);
                } catch (error) {
                    console.error("Error fetching bucket list:", error);
                    showError(t('errorFetchingBucketList', 'Failed to fetch asset data'), t('error', 'Error'));
                }
            })();
        }
    }, [selectedAsset, setSelectedAsset]);

    useEffect(() => {
        if (showToast) {
            setTimeout(() => setShowToast(false), 5000)
        }
    }, [showToast])

    const tableHeader = useMemo(
        () => getTableHeaders(locationHistoryTableHeaders),
        []
    )

    const goToHomepage = () => {
        navigate(routePath.HOMEPAGE)
    }

    const pagedData = useMemo(() => {
        if (Array.isArray(locationHistory) && locationHistory.length > 0) {
            // Filter out any undefined/null items
            const filtered = locationHistory.filter(Boolean);
            // Sort by created_at descending (latest first)
            const sorted = [...filtered].sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );
            // Format created_at for display
            for (const data of sorted) {
                data.created_at = dayjs(data.created_at).format('lll');
            }
            return groupData(sorted, rowCount);
        } else {
            return [];
        }
    }, [locationHistory, rowCount]);

    // Common function to process API response data
    const processLocationHistoryData = (nodes: any[]): LocationHistoryType[] => {
        return nodes
            .filter((node: any) => 
                node.location.latitude !== null && 
                node.location.latitude !== undefined && 
                node.location.longitude !== null && 
                node.location.longitude !== undefined
            )
            .map((node: any, index: number): LocationHistoryType => ({
                id: index,
                tags_location_id: 0, // Default value since not provided by API
                tag_id: 0, // Default value since not provided by API
                latitude: node.location.latitude,
                longitude: node.location.longitude,
                location_name: !node.location.location_name || node.location.location_name === "" || node.location.location_name === null ? "Fetching..." : node.location.location_name,
                created_at: node.reportedAt,
            }));
    }

    const fetchLocationHistory = (params: LocationHistoryParamsType) => {
        assetsApi
            .getLocationHistoryGraph(params)
            .then((result: any) => {
                setSelectedPing(undefined)
                if (result.data.data.data.readings.nodes.length > 0) {
                    const locationHistoryData = processLocationHistoryData(result.data.data.data.readings.nodes);
                    setLocationHistory(locationHistoryData);
                    
                    // Fetch addresses for locations that are still "Fetching..."
                    fetchAddressesForMissingLocations(locationHistoryData);
                    
                if (result.data.data.data.readings.pageInfo) {
                    setPaginationData(result.data.data.data.readings.pageInfo)
                }
                } else {
                    setLocationHistory([])
                }
                setIsLoading(false)
            })
            .catch(() => {
                setIsLoading(false)
            })
    }

    const fetchAddressesForMissingLocations = async (historyData: LocationHistoryType[]) => {
        try {
            // Filter locations that need address lookup
            const locationsNeedingAddresses = historyData.filter(item => 
                item.location_name === "Fetching..." && 
                item.latitude && 
                item.longitude
            );

            if (locationsNeedingAddresses.length === 0) return;

            // Build the coordinates payload
            const coordinates = locationsNeedingAddresses.map(item => ({
                latitude: item.latitude,
                longitude: item.longitude,
                temp_id: String(item.id)
            }));

            const payload = { coordinates };

            // Get auth tokens
            const accessToken = StorageService.getItem(ACCESS_TKN_KEY) || '';
            const refreshToken = StorageService.getItem(REFRESH_TOKEN) || '';

            // Call the API
            const response = await assetsApi.getAddressFromCoordinates(payload, { accessToken, refreshToken });

            if (response.data?.data) {
                // Update location history with fetched addresses
                const updatedHistory = historyData.map(item => {
                    const foundAddress = response.data.data.find((addr: any) =>
                        String(addr.temp_id) === String(item.id)
                    );
                    
                    if (foundAddress?.location_name) {
                        return {
                            ...item,
                            location_name: foundAddress.location_name
                        };
                    }
                    
                    return item;
                });                
                // Force update the state
                setLocationHistory([...updatedHistory]);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to fetch location addresses';
            showError(t('errorFetchingAddresses', errorMessage), t('error', 'Error'));
        }
    }

    const onHandlePagination = (onPageChange:string) => {
        setIsPaginationLoading(true);
        const asset = getSelectedAsset(selectedAsset, setSelectedAsset);
        const formattedStartDate = dayjs(dateRange[0]).format('MM/DD/YYYY');
        const formattedEndDate = dayjs(dateRange[1]).format('MM/DD/YYYY');
        const params: any = {
            asset_id: asset.asset_id,
            start_date: formattedStartDate,
            end_date: formattedEndDate,
            page_action: onPageChange,
        };

        // Add cursor parameter based on page action
        if (onPageChange === 'next') {
            params.end_cursor = paginationData.endCursor;
        } else {
            params.start_cursor = paginationData.startCursor;
        }
        
        assetsApi
            .getLocationHistoryGraph(params)
            .then((result: any) => {
                setSelectedPing(undefined)
                if (result.data.data.data.readings.nodes.length > 0) {
                    const locationHistoryData = processLocationHistoryData(result.data.data.data.readings.nodes);
                    setLocationHistory(locationHistoryData);
                    
                    // Fetch addresses for locations that are still "Fetching..."
                    fetchAddressesForMissingLocations(locationHistoryData);
                    
                if (result.data.data.data.readings.pageInfo) {
                    setPaginationData(result.data.data.data.readings.pageInfo);
                }
                } else {
                    setLocationHistory([])
                }
                setIsPaginationLoading(false)
            })
            .catch(() => {
                setIsPaginationLoading(false)
            })
    }
    const showValidationMessage = () => {
        setToastData({
            variant: 'critical',
            message: t('dateRangeValidation'),
        })
        setShowToast(true)
    }

    const getSelectedAsset = (
        currentAsset: any | null,
        setAssetFn: (asset: any) => void
    ): any | null => {
        // Return current asset if it exists
        if (currentAsset) {
            return currentAsset;
        }

        // Try to get from sessionStorage
        const sessionData: string | null = sessionStorage.getItem(SELECTED_ASSET);

        if (sessionData) {
            try {
                const parsedAsset = JSON.parse(sessionData);
                // Update state with the parsed asset
                setAssetFn(parsedAsset);
                return parsedAsset;
            } catch (error) {
                console.error('Error parsing session data:', error);
                return null;
            }
        } else {
            goToHomepage();
        }
    };

    const applyDateFilter = () => {
        setIsLoading(true);
        const formattedStartDate = dayjs(dateRange[0]).format('MM/DD/YYYY');
        const formattedEndDate = dayjs(dateRange[1]).format('MM/DD/YYYY');

        // Get asset using the reusable function
        const asset = getSelectedAsset(selectedAsset, setSelectedAsset);

        if (!asset) {
            // Handle case when no asset is available
            setIsLoading(false);
            return;
        }

        const params = {
            asset_id: asset.asset_id,
            start_date: formattedStartDate,
            end_date: formattedEndDate,
        };

        if (new Date(params.start_date) <= new Date(params.end_date)) {
            fetchLocationHistory(params);
        } else {
            showValidationMessage();
            setIsLoading(false);
        }
    }
    const onDateRangePickerChange = (
        value: [Date | null, Date | null] | null,
        _event: React.SyntheticEvent<Element, Event>
    ) => {
        if (value && Array.isArray(value) && value[0] && value[1]) {
            setDateRange([value[0], value[1]]);
        }
    }

    const tableRowClick = (event: any) => {
        if (selectedPing?.id == event.id.value) {
            setSelectedPing(undefined)
            logAnalyticsEvent('web_user_unselect_a_ping_from_table', { description: 'User unselect a ping from the table' });
        } else {
            const selectedPingData: SelectedPingType = {
                id: event.id.value,
                lat: event.latitude.value,
                lng: event.longitude.value,
            }
            logAnalyticsEvent('web_user_select_a_ping_from_table', { description: 'User selected a ping from the table' });
            setSelectedPing(selectedPingData)
        }
    }

    useEffect(() => {
        applyDateFilter()
    }, []);

    // Effect to handle row selection styling
    useEffect(() => {
        if (!selectedPing) {
            // Remove all selection styling if nothing is selected
            const tableRows = document.querySelectorAll('.location-history table tbody tr');
            for (const htmlRow of tableRows) {
                (htmlRow as HTMLElement).classList.remove('selected-row');
                (htmlRow as HTMLElement).style.backgroundColor = '';
            }
            return;
        }

        // Find the matching row by comparing data
        const selectedIndex = locationHistory.findIndex(item => item.id === selectedPing.id);
        
        if (selectedIndex !== -1) {
            // Get all tbody rows 
            const allRows = Array.from(document.querySelectorAll('.location-history table tbody tr'));
            
            // Clear all existing selections first
            for (const htmlRow of allRows) {
                (htmlRow as HTMLElement).classList.remove('selected-row');
                (htmlRow as HTMLElement).style.backgroundColor = '';
            }
            
            // Apply selection to the correct row - add 1 to skip header row
            const targetIndex = selectedIndex + 1;
            if (allRows[targetIndex]) {
                const targetRow = allRows[targetIndex] as HTMLElement;
                targetRow.classList.add('selected-row');
                targetRow.style.backgroundColor = '#E6F3FF';
            }
        }
    }, [selectedPing, locationHistory]);

    const isDateRangeValid = useMemo(() => {
        if (!dateRange[0] || !dateRange[1]) return false;
        
        // Use dayjs for consistent date comparison (same logic as shouldDisableDate)
        const today = dayjs().startOf('day');
        const threeYearsAgo = dayjs().subtract(3, 'years').startOf('day');
        
        const startDate = dayjs(dateRange[0]).startOf('day');
        const endDate = dayjs(dateRange[1]).startOf('day');
        
        // Check if start date is valid
        const shouldDisableStart = startDate.isBefore(threeYearsAgo) || startDate.isAfter(today);
        // Check if end date is valid  
        const shouldDisableEnd = endDate.isBefore(threeYearsAgo) || endDate.isAfter(today);
        
        // Both dates should NOT be disabled for the range to be valid
        return !shouldDisableStart && !shouldDisableEnd;
    }, [dateRange]);

    const renderTableContent = () => {
        if (isLoading) {
            return (
                <div className="spinner">
                    <SpinnerCircular darkMode={darkMode} />
                </div>
            );
        }
        
        if (pagedData.length > 0 && tableHeader.length > 0) {
            return (
                <div>
                    <div className={`notes-section ${darkMode ? 'dark-mode' : ''}`}>
                        <p>
                            <strong>{t('notes')}:</strong> {t('locationHistoryNote')}
                        </p>
                    </div>
                    <div style={{
                        height: '400px',
                        overflow: 'auto',
                        border: `1px solid ${darkMode ? '#4A5568' : '#E2E8F0'}`,
                        borderRadius: '6px',
                        backgroundColor: darkMode ? '#2D3748' : '#FFFFFF'
                    }}>
                        <Table
                            darkMode={darkMode}
                            header={tableHeader}
                            source={locationHistory}
                            title={t('locationHistory')}
                            tableType={'small'}
                            rowClickable={true}
                            onRowClick={(e: any) => tableRowClick(e)}
                            primaryActionBtnStatus={false}
                            key={currentPage}
                        />
                    </div>
                    <div style={{ 
                        display: 'flex',  
                        justifyContent: 'center',
                        gap: '16px',
                        alignItems: 'center',
                        marginTop: '16px',
                        padding: '0 16px',
                        marginBottom: '16px',
                    }}>
                        {isPaginationLoading ? (
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px',
                                color: darkMode ? '#E2E8F0' : '#4A5568',
                                fontSize: '14px'
                            }}>
                                <SpinnerCircular darkMode={darkMode} size="sm" />
                                <span>Loading...</span>
                            </div>
                        ) : (
                            <>
                                <Button
                                    label={t('previous') || 'Previous'}
                                    onClick={() => onHandlePagination('previous')}
                                    outerContainerPadding="20px"
                                    size="sm"
                                    variant="primary"
                                    disabled={!paginationData.hasPreviousPage}
                                />
                                <Button
                                    label={t('next') || 'Next'}
                                    onClick={() => onHandlePagination('next')}
                                    outerContainerPadding="20px"
                                    size="sm"
                                    variant="primary"
                                    disabled={!paginationData.hasNextPage}
                                />
                            </>
                        )}
                    </div>
                </div>
            );
        }
        
        return (
            <div className="noResults">
                <p className={darkMode ? 'dark' : 'light'}>{t('noResultsFound')}</p>
            </div>
        );
    };
    return (
        <>
            <div className="location-history" >
                <div className='location-history-grid' style={{ backgroundColor: darkMode ? '#3B434C' : '#F6F7F8' }}>
                    <div className="list-grid">
                        <div className="date-picker">
                            <DateRangePicker
                                format='yyyy/MM/dd'
                                character=' - '
                                onClean={() => {
                                    setDateRange([sevenDaysAgo.toDate(), dayjs().toDate()]);
                                }}
                                onChange={onDateRangePickerChange}
                                value={dateRange}
                                shouldDisableDate={(date) => {
                                    // Use dayjs for consistent date comparison
                                    const today = dayjs().startOf('day');
                                    const threeYearsAgo = dayjs().subtract(3, 'years').startOf('day');
                                    const checkDate = dayjs(date).startOf('day');
                                    
                                    return checkDate.isBefore(threeYearsAgo) || checkDate.isAfter(today);
                                }}
                            />
                            <Button
                                darkMode={darkMode}
                                label={t('apply')}
                                size={'sm'}
                                onClick={() => {
                                    applyDateFilter()
                                    logAnalyticsEvent('web_user_filter_location_history', { description: 'User filtered the location history' });
                                }}
                                full={false}
                                variant={'primary'}
                                disabled={!isDateRangeValid}
                            />
                        </div>
                        <div className="table">
                            {renderTableContent()}
                        </div>
                    </div>
                    <div className='map-grid'>
                        <DisplayMap
                            isLocationHistory={true}
                            locationHistory={locationHistory}
                            selectedPing={selectedPing}
                            tagData={tagData}
                            zoom={selectedPing ? 20 : 5}
                        />
                    </div>
                </div>
            </div>
            {showToast ? (
                <div className="toast-container">
                    <Notification
                        darkMode={darkMode}
                        title={toastData?.variant === "success" ? "Success" : "Fail"}
                        textContent={toastData?.message}
                        variant={toastData?.variant}
                        onClose={() => setShowToast(false)}
                    />
                </div>
            ) : (
                <></>
            )}
        </>
    )
}